import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { checklistFilterLabel } from '@/utils/styleHelpers';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  getChecklistTemplates, getChecklists, getChecklist, createChecklistAssignment, 
  updateChecklist, renameChecklist, seedChecklistTemplates, getAllUsers, deleteChecklist,
  getCustomChecklists, getTeams, archiveResource 
} from '@/services/api';
import { UserMultiSelect } from '@/components/UserMultiSelect';
import { UserAvatar, AvatarStack } from '@/components/UserAvatar';
import { 
  Plus, ClipboardCheck, FileText, CheckCircle, Clock, Edit, 
  ChevronRight, AlertCircle, Star, Users, Calendar, Pencil, Save, X, Trash2,
  Table as TableIcon, Lock, Archive
} from 'lucide-react';

// Checklists List Page
export const ChecklistsListPage = () => {
  const { isMasterAdmin, isAdmin, user, isGuest } = useAuth();
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customChecklists, setCustomChecklists] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  
  const [newAssignment, setNewAssignment] = useState({
    template_id: '',
    assigned_to_ids: [],
    team_ids: [],
    custom_name: '',
    instructions: '',
    due_date: '',
    priority: 'medium',
    facility: '',
    location: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [checklistsData, templatesData, customData] = await Promise.all([
        getChecklists(null, null),
        getChecklistTemplates(),
        getCustomChecklists().catch(() => []),
      ]);
      setChecklists(checklistsData);
      setTemplates(templatesData);
      setCustomChecklists(customData || []);
      
      if (isMasterAdmin) {
        const [usersData, teamsData] = await Promise.all([
          getAllUsers(),
          getTeams().catch(() => []),
        ]);
        setUsers(usersData);
        setTeams(teamsData || []);
      }
    } catch (error) {
      toast.error('Failed to load checklists');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedTemplates = async () => {
    try {
      await seedChecklistTemplates();
      toast.success('Checklist templates seeded');
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateAssignment = async () => {
    const totalAssignees = (newAssignment.assigned_to_ids?.length || 0) + (newAssignment.team_ids?.length || 0);
    if (!newAssignment.template_id || totalAssignees === 0) {
      toast.error('Please select a template and at least one user or team');
      return;
    }
    
    try {
      await createChecklistAssignment(newAssignment);
      const teamCount = newAssignment.team_ids?.length || 0;
      const userCount = newAssignment.assigned_to_ids?.length || 0;
      const parts = [];
      if (userCount) parts.push(`${userCount} user${userCount !== 1 ? 's' : ''}`);
      if (teamCount) parts.push(`${teamCount} team${teamCount !== 1 ? 's' : ''}`);
      toast.success(`Checklist assigned to ${parts.join(' + ')}`);
      setShowAssignDialog(false);
      setNewAssignment({
        template_id: '',
        assigned_to_ids: [],
        team_ids: [],
        custom_name: '',
        instructions: '',
        due_date: '',
        priority: 'medium',
        facility: '',
        location: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteChecklist = async (e, checklistId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this checklist?')) return;
    
    try {
      await deleteChecklist(checklistId);
      toast.success('Checklist deleted');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete checklist');
    }
  };

  const handleArchiveChecklist = async (e, checklistId) => {
    e.stopPropagation();
    if (!window.confirm('Archive this checklist? It can be restored from the Archive page.')) return;
    try {
      await archiveResource('checklist', checklistId);
      toast.success('Checklist archived');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to archive checklist');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const myChecklists = checklists.filter(c => 
    c.assigned_to === user?.user_id ||
    c.created_by === user?.user_id ||
    (Array.isArray(c.assignees) && c.assignees.some(a => a.user_id === user?.user_id))
  );
  const allChecklists = isMasterAdmin ? checklists : myChecklists;
  const displayChecklists = statusFilter === 'all'
    ? allChecklists
    : allChecklists.filter(c => c.status === statusFilter);

  const filterLabel = checklistFilterLabel(statusFilter);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="checklists-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Checklists</h1>
            <p className="text-gray-500">
              {isMasterAdmin ? 'Manage and assign audit checklists' : 'Your assigned checklists'}
            </p>
          </div>
          <div className="flex gap-3 items-center">
            {isMasterAdmin && (
              <>
                <Button variant="outline" onClick={handleSeedTemplates}>
                  <FileText className="w-4 h-4 mr-2" /> Load Templates
                </Button>
                <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="assign-checklist-btn">
                      <Plus className="w-4 h-4 mr-2" /> Assign Checklist
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Assign Checklist</DialogTitle>
                      <DialogDescription>
                        Select a template and assign it to a user with optional instructions
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Checklist Template *</Label>
                        <Select 
                          value={newAssignment.template_id} 
                          onValueChange={(v) => {
                            const template = templates.find(t => t.template_id === v);
                            setNewAssignment({
                              ...newAssignment, 
                              template_id: v,
                              custom_name: template?.name || ''
                            });
                          }}
                        >
                          <SelectTrigger data-testid="template-select">
                            <SelectValue placeholder="Select template" />
                          </SelectTrigger>
                          <SelectContent>
                            {templates.map(t => (
                              <SelectItem key={t.template_id} value={t.template_id}>
                                {t.name} ({t.items?.length || 0} items)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Assign to users <span className="text-xs text-muted-foreground font-normal">(select one or more)</span></Label>
                        <UserMultiSelect
                          users={users}
                          selectedIds={newAssignment.assigned_to_ids}
                          onChange={(ids) => setNewAssignment({ ...newAssignment, assigned_to_ids: ids })}
                          placeholder="Select assignees..."
                          testIdPrefix="checklist-assignee"
                        />
                      </div>

                      <div>
                        <Label>Assign to teams <span className="text-xs text-muted-foreground font-normal">(optional — all current members will receive it)</span></Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-between font-normal h-9" data-testid="checklist-team-trigger">
                              {newAssignment.team_ids?.length
                                ? `${newAssignment.team_ids.length} team${newAssignment.team_ids.length !== 1 ? 's' : ''} selected`
                                : 'Select teams...'}
                              <ChevronRight className="h-4 w-4 opacity-50 rotate-90" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 max-h-72 overflow-y-auto p-2" align="start">
                            {teams.length === 0 ? (
                              <p className="text-xs text-muted-foreground px-2 py-3 text-center">No teams yet — create one in Teams.</p>
                            ) : teams.map(t => {
                              const checked = (newAssignment.team_ids || []).includes(t.team_id);
                              const memberCount = (t.members?.length || 0) + (t.employee_members?.length || 0);
                              return (
                                <label
                                  key={t.team_id}
                                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/60 cursor-pointer text-sm"
                                  data-testid={`checklist-team-opt-${t.team_id}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      const ids = newAssignment.team_ids || [];
                                      setNewAssignment({
                                        ...newAssignment,
                                        team_ids: e.target.checked ? [...ids, t.team_id] : ids.filter(x => x !== t.team_id),
                                      });
                                    }}
                                    className="h-4 w-4"
                                  />
                                  <Users className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                  <span className="flex-1 truncate">{t.name}</span>
                                  <span className="text-[10px] text-muted-foreground flex-shrink-0">{memberCount}</span>
                                </label>
                              );
                            })}
                            {(newAssignment.team_ids?.length || 0) > 0 && (
                              <Button variant="ghost" size="sm" className="w-full mt-1 text-xs" onClick={() => setNewAssignment({ ...newAssignment, team_ids: [] })}>
                                Clear teams
                              </Button>
                            )}
                          </PopoverContent>
                        </Popover>
                      </div>
                      
                      <div>
                        <Label>Custom Name (editable)</Label>
                        <Input 
                          value={newAssignment.custom_name}
                          onChange={(e) => setNewAssignment({...newAssignment, custom_name: e.target.value})}
                          placeholder="Override template name"
                          data-testid="custom-name-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Leave blank to use template name</p>
                      </div>
                      
                      <div>
                        <Label>Instructions / Notes</Label>
                        <Textarea 
                          value={newAssignment.instructions}
                          onChange={(e) => setNewAssignment({...newAssignment, instructions: e.target.value})}
                          placeholder="Add specific instructions for this assignment..."
                          rows={3}
                          data-testid="instructions-input"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Due Date</Label>
                          <Input 
                            type="date"
                            value={newAssignment.due_date}
                            onChange={(e) => setNewAssignment({...newAssignment, due_date: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Priority</Label>
                          <Select 
                            value={newAssignment.priority} 
                            onValueChange={(v) => setNewAssignment({...newAssignment, priority: v})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Facility (optional)</Label>
                          <Input 
                            value={newAssignment.facility}
                            onChange={(e) => setNewAssignment({...newAssignment, facility: e.target.value})}
                            placeholder="Building A"
                          />
                        </div>
                        <div>
                          <Label>Location (optional)</Label>
                          <Input 
                            value={newAssignment.location}
                            onChange={(e) => setNewAssignment({...newAssignment, location: e.target.value})}
                            placeholder="Aisle 5"
                          />
                        </div>
                      </div>
                      
                      <Button onClick={handleCreateAssignment} className="w-full" data-testid="submit-assignment">
                        Assign Checklist
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>

        {/* Stats - Clickable filter cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'all' ? 'ring-2 ring-blue-500 shadow-md' : ''}`}
            onClick={() => setStatusFilter('all')}
            data-testid="stat-total"
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <ClipboardCheck className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{allChecklists.length}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'pending' ? 'ring-2 ring-yellow-500 shadow-md' : ''}`}
            onClick={() => setStatusFilter('pending')}
            data-testid="stat-pending"
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{allChecklists.filter(c => c.status === 'pending').length}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'in_progress' ? 'ring-2 ring-blue-500 shadow-md' : ''}`}
            onClick={() => setStatusFilter('in_progress')}
            data-testid="stat-in-progress"
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{allChecklists.filter(c => c.status === 'in_progress').length}</p>
                <p className="text-sm text-muted-foreground">In Progress</p>
              </div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'completed' ? 'ring-2 ring-green-500 shadow-md' : ''}`}
            onClick={() => setStatusFilter('completed')}
            data-testid="stat-completed"
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{allChecklists.filter(c => c.status === 'completed').length}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Templates Section */}
        {isMasterAdmin && templates.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Available Templates</CardTitle>
              <CardDescription>Click "Assign Checklist" to assign a template to a user</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {templates.slice(0, 6).map(template => (
                  <div 
                    key={template.template_id}
                    className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setNewAssignment({...newAssignment, template_id: template.template_id, custom_name: template.name});
                      setShowAssignDialog(true);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">{template.name}</h4>
                      <Badge variant="outline" className="text-xs">{template.items?.length || 0} items</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{template.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Custom Checklists Section — shown for everyone (read-only for non-admins) */}
        {(customChecklists.length > 0 || isAdmin) && (
          <Card data-testid="custom-checklists-section">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TableIcon className="h-5 w-5 text-[#2D8C8C]" />
                    Custom Checklists
                    {!isAdmin && <Badge variant="outline" className="gap-1 text-xs ml-1"><Lock className="h-3 w-3" /> Read-only</Badge>}
                  </CardTitle>
                  <CardDescription>
                    {isAdmin
                      ? 'Free-form tables with live collaboration. Click any one to open.'
                      : 'All custom tables across the company — view-only for your role.'}
                  </CardDescription>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => navigate('/custom-checklists?new=1')} data-testid="cc-new-btn">
                      <Plus className="h-4 w-4 mr-1" /> New custom checklist
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => navigate('/custom-checklists')} data-testid="cc-manage-btn">
                      Manage <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {customChecklists.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <TableIcon className="h-8 w-8 mx-auto opacity-30 mb-2" />
                  <p className="text-sm">No custom checklists yet.</p>
                  {isAdmin && (
                    <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate('/custom-checklists?new=1')}>
                      <Plus className="h-4 w-4 mr-1" /> Create your first one
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {customChecklists.map(cc => (
                  <button
                    key={cc.checklist_id}
                    type="button"
                    onClick={() => navigate(`/custom-checklists/${cc.checklist_id}`)}
                    className="text-left p-3 border rounded-lg hover:bg-muted/40 hover:border-[#2D8C8C]/40 transition-colors"
                    data-testid={`cc-card-${cc.checklist_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-medium text-sm truncate">{cc.title}</h4>
                      <Badge variant="outline" className="text-[10px] flex-shrink-0">
                        {(cc.rows?.length) || 0} row{(cc.rows?.length) === 1 ? '' : 's'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-1.5">
                      <span>{(cc.cols?.length) || 0} cols</span>
                      {cc.updated_at && (
                        <>
                          <span>·</span>
                          <span>Updated {new Date(cc.updated_at).toLocaleDateString()}</span>
                        </>
                      )}
                      {cc.created_by_name && (
                        <>
                          <span>·</span>
                          <span className="truncate">by {cc.created_by_name}</span>
                        </>
                      )}
                    </div>
                  </button>
                ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Checklists List */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : displayChecklists.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ClipboardCheck className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">
                {statusFilter === 'all' ? 'No checklists found' : `No ${statusFilter.replace('_', ' ')} checklists`}
              </p>
              {isMasterAdmin && statusFilter === 'all' && <p className="text-sm text-muted-foreground mt-1">Click "Assign Checklist" to create one</p>}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold" data-testid="checklist-list-heading">{filterLabel}</h2>
              <p className="text-sm text-muted-foreground">{displayChecklists.length} checklist{displayChecklists.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="grid gap-4">
              {displayChecklists.map(checklist => (
              <Card 
                key={checklist.checklist_id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/checklists/${checklist.checklist_id}`)}
                data-testid={`checklist-card-${checklist.checklist_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{checklist.display_name || checklist.title}</h3>
                        <Badge className={getStatusColor(checklist.status)}>
                          {checklist.status?.replace('_', ' ')}
                        </Badge>
                        <Badge className={getPriorityColor(checklist.priority)}>
                          {checklist.priority}
                        </Badge>
                        <Badge variant="outline">{checklist.checklist_type}</Badge>
                      </div>
                      
                      {checklist.instructions && (
                        <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                          <span className="font-medium">Instructions:</span> {checklist.instructions}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-2" data-testid={`checklist-assignees-${checklist.checklist_id}`}>
                          {Array.isArray(checklist.assignees) && checklist.assignees.length > 0 ? (
                            <>
                              <AvatarStack users={checklist.assignees} size="xs" max={3} />
                              <span className="text-xs">
                                {checklist.assignees.length <= 2
                                  ? checklist.assignees.map(a => a.name).join(', ')
                                  : `${checklist.assignees[0].name} +${checklist.assignees.length - 1} more`}
                              </span>
                            </>
                          ) : (
                            <>
                              <Users className="w-4 h-4" />
                              <span>{checklist.assigned_to_name || 'Unassigned'}</span>
                            </>
                          )}
                        </span>
                        {checklist.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Due: {new Date(checklist.due_date).toLocaleDateString()}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <ClipboardCheck className="w-4 h-4" />
                          {checklist.checked_items}/{checklist.total_items} items
                        </span>
                        {checklist.total_score > 0 && (
                          <span className="flex items-center gap-1">
                            <Star className="w-4 h-4" />
                            Score: {checklist.total_score}/{checklist.max_score}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(isMasterAdmin || isAdmin) && checklist.status === 'completed' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          onClick={(e) => handleArchiveChecklist(e, checklist.checklist_id)}
                          data-testid={`archive-checklist-${checklist.checklist_id}`}
                          title="Archive checklist"
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                      {(isMasterAdmin || isAdmin) && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => handleDeleteChecklist(e, checklist.checklist_id)}
                          data-testid={`delete-checklist-${checklist.checklist_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <Progress value={checklist.completion_percentage} className="h-2" />
                    <p className="text-xs text-muted-foreground mt-1">{Math.round(checklist.completion_percentage)}% complete</p>
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Checklist Detail Page
export const ChecklistDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isMasterAdmin } = useAuth();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    loadChecklist();
  }, [id]);

  // Real-time sync: poll every 4s, skip when user is actively saving or editing name
  useEffect(() => {
    const interval = setInterval(() => {
      if (saving || editingName) return;
      getChecklist(id).then(d => {
        setChecklist(prev => {
          if (!prev) return d;
          // Don't clobber if updated_at is the same to avoid re-renders
          return JSON.stringify(prev) === JSON.stringify(d) ? prev : d;
        });
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(interval);
  }, [id, saving, editingName]);

  const loadChecklist = async () => {
    try {
      const data = await getChecklist(id);
      setChecklist(data);
      setNewName(data.display_name || data.title);
    } catch (error) {
      toast.error('Failed to load checklist');
      navigate('/checklists');
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = async (itemId, field, value) => {
    if (!checklist) return;
    
    const updatedItems = checklist.items.map(item => {
      if (item.item_id === itemId) {
        return { ...item, [field]: value };
      }
      return item;
    });
    
    setChecklist({ ...checklist, items: updatedItems });
    
    // Auto-save
    setSaving(true);
    try {
      await updateChecklist(id, { items: updatedItems });
      loadChecklist(); // Refresh to get updated scores
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async () => {
    if (!newName.trim()) return;
    
    try {
      await renameChecklist(id, newName);
      toast.success('Checklist renamed');
      setEditingName(false);
      loadChecklist();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">Loading...</div>
      </DashboardLayout>
    );
  }

  if (!checklist) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">Checklist not found</div>
      </DashboardLayout>
    );
  }

  // Group items by category
  const categories = [...new Set(checklist.items.map(item => item.category || 'General'))];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="checklist-detail-page">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <Button variant="ghost" onClick={() => navigate('/checklists')} className="mb-2">
              ← Back to Checklists
            </Button>
            <div className="flex items-center gap-3">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input 
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="text-xl font-bold w-64"
                    autoFocus
                  />
                  <Button size="sm" onClick={handleRename}>
                    <Save className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <h1 className="text-2xl font-bold">{checklist.display_name || checklist.title}</h1>
                  <Button size="sm" variant="ghost" onClick={() => setEditingName(true)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={checklist.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                {checklist.status}
              </Badge>
              <Badge variant="outline">{checklist.checklist_type}</Badge>
              {saving && <Badge variant="secondary">Saving...</Badge>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-primary">
              {Math.round(checklist.completion_percentage)}%
            </div>
            <p className="text-sm text-muted-foreground">
              {checklist.checked_items}/{checklist.total_items} items
            </p>
            {checklist.total_score > 0 && (
              <p className="text-sm font-medium mt-1">
                Score: {checklist.total_score}/{checklist.max_score}
              </p>
            )}
          </div>
        </div>

        {/* Instructions */}
        {checklist.instructions && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h3 className="font-medium text-blue-800 mb-1">Instructions</h3>
              <p className="text-blue-700">{checklist.instructions}</p>
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-sm text-muted-foreground">{Math.round(checklist.completion_percentage)}%</span>
            </div>
            <Progress value={checklist.completion_percentage} className="h-3" />
          </CardContent>
        </Card>

        {/* Checklist Items by Category */}
        {categories.map(category => (
          <Card key={category}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{category}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {checklist.items
                .filter(item => (item.category || 'General') === category)
                .map((item, index) => (
                  <div key={item.item_id} className="border rounded-lg p-4">
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={item.checked}
                        onCheckedChange={(checked) => handleItemChange(item.item_id, 'checked', checked)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <p className={`font-medium ${item.checked ? 'line-through text-gray-400' : ''}`}>
                          {item.text}
                        </p>
                        
                        <div className="flex items-center gap-4 mt-3">
                          {/* Rating */}
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">Rating:</Label>
                            <Select 
                              value={item.rating !== null && item.rating !== undefined ? String(item.rating) : "na"} 
                              onValueChange={(v) => handleItemChange(item.item_id, 'rating', v === 'na' ? null : parseInt(v))}
                            >
                              <SelectTrigger className="w-20 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="na">N/A</SelectItem>
                                <SelectItem value="1">1</SelectItem>
                                <SelectItem value="2">2</SelectItem>
                                <SelectItem value="3">3</SelectItem>
                                <SelectItem value="4">4</SelectItem>
                                <SelectItem value="5">5</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Y/N/P/NA Response */}
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">Response:</Label>
                            <Select 
                              value={item.response || 'none'} 
                              onValueChange={(v) => handleItemChange(item.item_id, 'response', v === 'none' ? null : v)}
                            >
                              <SelectTrigger className="w-20 h-8">
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">—</SelectItem>
                                <SelectItem value="Y">Yes</SelectItem>
                                <SelectItem value="N">No</SelectItem>
                                <SelectItem value="P">Partial</SelectItem>
                                <SelectItem value="NA">N/A</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div className="mt-3">
                          <Input
                            placeholder="Add notes..."
                            value={item.notes || ''}
                            onChange={(e) => handleItemChange(item.item_id, 'notes', e.target.value)}
                            className="text-sm h-8"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </CardContent>
          </Card>
        ))}

        {/* Meta Info */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Assigned To</p>
                <div className="font-medium" data-testid="checklist-detail-assignees">
                  {Array.isArray(checklist.assignees) && checklist.assignees.length > 0
                    ? (
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {checklist.assignees.map(a => (
                          <span key={a.user_id} className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-0.5 rounded-full border bg-muted/30">
                            <UserAvatar user={a} size="xs" />
                            <span className="text-xs">{a.name}</span>
                          </span>
                        ))}
                      </div>
                    )
                    : (checklist.assigned_to_name || 'Unassigned')}
                </div>
              </div>
              <div>
                <p className="text-muted-foreground">Assigned By</p>
                <p className="font-medium">{checklist.assigned_by_name || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Due Date</p>
                <p className="font-medium">{checklist.due_date ? new Date(checklist.due_date).toLocaleDateString() : 'No deadline'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Created</p>
                <p className="font-medium">{new Date(checklist.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ChecklistsListPage;
